import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  FileText, 
  Plus, 
  Download, 
  Eye, 
  Sparkles, 
  Shield, 
  Users, 
  Lock, 
  ClipboardList,
  Clock,
  AlertTriangle,
  Heart,
  Laptop,
  Car,
  Home,
  GraduationCap,
  Scale,
  RefreshCw,
  Check,
  Copy,
  Printer,
  UserCheck,
  Brain,
  Building
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

// Policy template types with icons and descriptions - QCS/Bettal comprehensive level
const POLICY_TYPES = [
  { 
    id: 'safeguarding', 
    name: 'Safeguarding (Children & Adults)', 
    icon: Shield,
    description: 'Comprehensive child protection, DSL responsibilities, 6Rs framework, allegations management, LADO referrals',
    compliance: 'Children Act 2004, Care Act 2014, Working Together 2023, CQC Reg 13, Ofsted'
  },
  { 
    id: 'consent', 
    name: 'Consent Policy', 
    icon: ClipboardList,
    description: 'Informed consent, Gillick competence, Mental Capacity Act, parental responsibility, best interests',
    compliance: 'Mental Capacity Act 2005, Montgomery v Lanarkshire, CQC Reg 11'
  },
  { 
    id: 'data_protection', 
    name: 'Data Protection & Privacy', 
    icon: Lock,
    description: 'UK GDPR, data subject rights, breach procedures, retention, Caldicott principles',
    compliance: 'UK GDPR, Data Protection Act 2018, ICO Guidance'
  },
  { 
    id: 'clinical_documentation', 
    name: 'Clinical Documentation', 
    icon: FileText,
    description: 'SOAP notes, record keeping standards, timeframes, audits, retention (25 years)',
    compliance: 'HCPC Standards, CQC Reg 17, NHS Records Management Code'
  },
  { 
    id: 'waiting_list', 
    name: 'Waiting List Management', 
    icon: Clock,
    description: 'Priority categories (P1-P4), RTT standards, 12/23hr response times, KPIs',
    compliance: 'NHS RTT Standards, NHS Constitution, CQC Reg 9'
  },
  { 
    id: 'complaints', 
    name: 'Complaints Policy', 
    icon: AlertTriangle,
    description: 'Three-stage process, timescales, advocacy, learning from complaints, Ombudsman',
    compliance: 'CQC Regulation 16, Local Authority Social Services Regulations'
  },
  { 
    id: 'health_safety', 
    name: 'Health & Safety', 
    icon: Heart,
    description: 'Risk assessment, COSHH, manual handling, RIDDOR, fire safety, first aid',
    compliance: 'Health and Safety at Work Act 1974, Management Regulations 1999'
  },
  { 
    id: 'equality_diversity', 
    name: 'Equality, Diversity & Inclusion', 
    icon: Users,
    description: 'Protected characteristics, reasonable adjustments, anti-discrimination, PSED',
    compliance: 'Equality Act 2010, Human Rights Act 1998, CQC'
  },
  { 
    id: 'online_safety', 
    name: 'Online Safety & Telehealth', 
    icon: Laptop,
    description: 'Video consultations, platform security, online safeguarding, consent, data security',
    compliance: 'UK GDPR, HCPC Standards, CQC, Computer Misuse Act'
  },
  { 
    id: 'lone_working', 
    name: 'Lone Working Policy', 
    icon: Car,
    description: 'Risk assessment, check-in procedures, home visits, emergency protocols',
    compliance: 'Health and Safety at Work Act, Management Regulations'
  },
  { 
    id: 'supervision', 
    name: 'Clinical Supervision', 
    icon: GraduationCap,
    description: 'Clinical, management and safeguarding supervision, frequency, records, contracts',
    compliance: 'HCPC Standards of Proficiency, CQC Regulation 18'
  },
  { 
    id: 'whistleblowing', 
    name: 'Whistleblowing (Raising Concerns)', 
    icon: Scale,
    description: 'Qualifying disclosures, protection from detriment, prescribed persons, procedure',
    compliance: 'Public Interest Disclosure Act 1998, CQC Reg 17'
  },
  { 
    id: 'recruitment', 
    name: 'Safer Recruitment & Vetting', 
    icon: UserCheck,
    description: 'DBS checks, references, interviews, barred lists, single central record',
    compliance: 'Safeguarding Vulnerable Groups Act, CQC Fit & Proper Persons'
  },
  { 
    id: 'incident_reporting', 
    name: 'Incident Reporting & Serious Incidents', 
    icon: AlertTriangle,
    description: 'Incident classification, RIDDOR, CQC notifications, duty of candour, RCA',
    compliance: 'CQC Regulation 20, RIDDOR 2013, Health and Safety Executive'
  },
  { 
    id: 'infection_control', 
    name: 'Infection Prevention & Control', 
    icon: Shield,
    description: 'Standard precautions, hand hygiene, PPE, cleaning, waste management, outbreaks',
    compliance: 'Health and Social Care Act 2008, CQC Regulation 12'
  },
  { 
    id: 'medication', 
    name: 'Medication Management', 
    icon: Heart,
    description: 'Administration, storage, controlled drugs, errors, covert administration',
    compliance: 'CQC Regulation 12, Misuse of Drugs Regulations, NICE'
  },
  { 
    id: 'mental_capacity', 
    name: 'Mental Capacity & Best Interests', 
    icon: Brain,
    description: 'Capacity assessment, best interests checklist, DoLS/LPS, advance decisions',
    compliance: 'Mental Capacity Act 2005, CQC Regulation 11, 13'
  },
  { 
    id: 'business_continuity', 
    name: 'Business Continuity', 
    icon: Building,
    description: 'Emergency planning, pandemic response, service continuity, disaster recovery',
    compliance: 'Civil Contingencies Act 2004, CQC'
  },
  { 
    id: 'prevent', 
    name: 'Prevent Duty (Counter-Terrorism)', 
    icon: Shield,
    description: 'Radicalisation risk assessment, Channel referrals, staff training, Prevent duty',
    compliance: 'Counter-Terrorism and Security Act 2015, Home Office Prevent Duty Guidance'
  },
  { 
    id: 'modern_slavery', 
    name: 'Modern Slavery & Human Trafficking', 
    icon: Users,
    description: 'Recognising signs, reporting procedures, supply chain due diligence',
    compliance: 'Modern Slavery Act 2015, Home Office Guidance'
  },
  { 
    id: 'social_media', 
    name: 'Social Media Policy', 
    icon: Laptop,
    description: 'Professional boundaries online, organisational accounts, staff personal use',
    compliance: 'UK GDPR, HCPC Standards, Employment Law'
  },
  { 
    id: 'custom', 
    name: 'Custom Policy', 
    icon: Plus,
    description: 'Create any policy based on your specific requirements - describe what you need',
    compliance: 'As specified by your requirements'
  }
];

export default function PolicyTemplatesPage() {
  const { api, user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [customRequirements, setCustomRequirements] = useState('');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Check if current user is Super Admin
  const SUPER_ADMIN_EMAIL = 'admin@icrcl.co.uk';
  const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/ai/policy-templates');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load policy templates');
    } finally {
      setLoading(false);
    }
  };

  const [generationProgress, setGenerationProgress] = useState('');
  const [taskId, setTaskId] = useState(null);

  // Poll for generation status
  const pollGenerationStatus = async (taskId) => {
    let attempts = 0;
    const maxAttempts = 120; // 2 minutes with 1 second intervals
    
    const poll = async () => {
      try {
        const response = await api.get(`/ai/policy-generation-status/${taskId}`);
        const { status, result, error } = response.data;
        
        if (status === 'completed') {
          toast.success('Policy template generated successfully!');
          setGenerating(false);
          setGenerateDialogOpen(false);
          setSelectedType('');
          setCustomRequirements('');
          setGenerationProgress('');
          setTaskId(null);
          fetchTemplates();
          
          // Show the generated template
          if (result) {
            setSelectedTemplate({
              ...result,
              template_type: result.template_type,
              status: 'draft'
            });
            setViewDialogOpen(true);
          }
          return;
        }
        
        if (status === 'failed') {
          // Check for budget-related errors
          if (error && (error.toLowerCase().includes('budget') || error.toLowerCase().includes('exceeded'))) {
            toast.error('LLM budget exceeded. Please add more balance to your Universal Key (Profile → Universal Key → Add Balance).');
          } else {
            toast.error(error || 'Policy generation failed. Please try again.');
          }
          setGenerating(false);
          setGenerationProgress('');
          setTaskId(null);
          return;
        }
        
        // Still processing
        attempts++;
        if (attempts >= maxAttempts) {
          toast.error('Policy generation timed out. Please try again.');
          setGenerating(false);
          setGenerationProgress('');
          setTaskId(null);
          return;
        }
        
        // Update progress message based on time elapsed
        if (attempts < 10) {
          setGenerationProgress('Starting policy generation...');
        } else if (attempts < 30) {
          setGenerationProgress('AI is creating your comprehensive policy...');
        } else if (attempts < 60) {
          setGenerationProgress('Generating detailed sections and compliance content...');
        } else {
          setGenerationProgress('Finalizing comprehensive policy document...');
        }
        
        // Continue polling
        setTimeout(poll, 1000);
      } catch (err) {
        console.error('Error polling status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          toast.error('Failed to check generation status');
          setGenerating(false);
          setGenerationProgress('');
          setTaskId(null);
        }
      }
    };
    
    poll();
  };

  const handleGeneratePolicy = async () => {
    if (!selectedType) {
      toast.error('Please select a policy type');
      return;
    }

    if (selectedType === 'custom' && !customRequirements.trim()) {
      toast.error('Please describe your custom policy requirements');
      return;
    }

    setGenerating(true);
    setGenerationProgress('Initiating policy generation...');
    
    try {
      const response = await api.post('/ai/generate-policy-template', {
        template_type: selectedType,
        format_style: 'uk_healthcare',
        custom_requirements: selectedType === 'custom' 
          ? `Create a policy for ICRCL (Innovative Care and Rehab Centre Limited), a UK-based children's therapy service. ${customRequirements}. Ensure compliance with all relevant UK healthcare regulations including CQC, Ofsted, and Home Office requirements where applicable.`
          : `For ICRCL (Innovative Care and Rehab Centre Limited), a UK-based children's therapy service. Must comply with all relevant UK regulations including CQC, Ofsted, Home Office, NHS standards. Include ICRCL company name throughout.`
      });

      // Check if we got a task_id (async response) or direct result
      if (response.data.task_id) {
        // Async flow - start polling
        setTaskId(response.data.task_id);
        toast.info('Policy generation started. This may take 1-2 minutes for comprehensive policies.');
        pollGenerationStatus(response.data.task_id);
      } else if (response.data.content) {
        // Direct result (shouldn't happen with new async flow, but handle for safety)
        toast.success('Policy template generated successfully!');
        setGenerating(false);
        setGenerateDialogOpen(false);
        setSelectedType('');
        setCustomRequirements('');
        setGenerationProgress('');
        fetchTemplates();
        
        setSelectedTemplate({
          ...response.data,
          template_type: selectedType,
          status: 'draft'
        });
        setViewDialogOpen(true);
      }
      
    } catch (error) {
      console.error('Generation failed:', error);
      setGenerating(false);
      setGenerationProgress('');
      
      if (error.response?.status === 500 && error.response?.data?.detail?.includes('Budget')) {
        toast.error('LLM budget exceeded. Please add balance to your Universal Key.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate policy template');
      }
    }
  };

  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setViewDialogOpen(true);
  };

  const handleDownload = (template) => {
    const blob = new Blob([template.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ICRCL_${template.template_type.replace(/\s+/g, '_')}_Policy.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Policy template downloaded');
  };

  const handleCopyToClipboard = async (content) => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handlePrint = (template) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>ICRCL - ${template.template_type} Policy</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #1e3a5f; }
            h2 { color: #2d4a6f; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
          </style>
        </head>
        <body>
          <div style="white-space: pre-wrap;">${template.content.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getPolicyIcon = (type) => {
    const policy = POLICY_TYPES.find(p => p.id === type.toLowerCase().replace(/\s+/g, '_'));
    return policy?.icon || FileText;
  };

  // Check if user is super admin
  if (user?.email?.toLowerCase() !== 'admin@icrcl.co.uk') {
    return (
      <div className="p-6">
        <Card className="max-w-lg mx-auto">
          <CardContent className="py-12 text-center">
            <Lock className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Access Restricted</h2>
            <p className="text-slate-500">Only Super Admin can access policy templates.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="w-7 h-7 text-[#1e3a5f]" />
            Policy Templates
          </h1>
          <p className="text-slate-500 mt-1">
            UK Healthcare compliant policy templates for ICRCL
          </p>
        </div>
        {isSuperAdmin && (
          <Button 
            onClick={() => setGenerateDialogOpen(true)}
            className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8f] hover:from-[#2d4a6f] hover:to-[#3d6a9f]"
            data-testid="generate-policy-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Generate New Policy
          </Button>
        )}
      </div>

      {/* Compliance Badges */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm font-medium text-emerald-800 mr-2">Compliance:</span>
            <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-700">CQC</Badge>
            <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-700">Ofsted</Badge>
            <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-700">Home Office</Badge>
            <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-700">UK GDPR</Badge>
            <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-700">NHS Standards</Badge>
            <Badge variant="outline" className="bg-white border-emerald-300 text-emerald-700">HCPC</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-2" />
          <p className="text-slate-500">Loading templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">No Policy Templates Yet</h3>
            <p className="text-slate-500 mb-4">
              {isSuperAdmin 
                ? "Generate your first UK Healthcare compliant policy template" 
                : "No policies have been created yet. Please contact the Super Admin."}
            </p>
            {isSuperAdmin && (
              <Button onClick={() => setGenerateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Generate Policy
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => {
            const IconComponent = getPolicyIcon(template.template_type);
            return (
              <Card 
                key={template.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => handleViewTemplate(template)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="w-10 h-10 rounded-lg bg-[#1e3a5f]/10 flex items-center justify-center">
                      <IconComponent className="w-5 h-5 text-[#1e3a5f]" />
                    </div>
                    <Badge 
                      variant={template.status === 'approved' ? 'default' : 'secondary'}
                      className={template.status === 'approved' ? 'bg-emerald-500' : ''}
                    >
                      {template.status || 'draft'}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg mt-3 group-hover:text-[#1e3a5f] transition-colors">
                    {template.template_type.charAt(0).toUpperCase() + template.template_type.slice(1).replace(/_/g, ' ')} Policy
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {template.source === 'prebuilt' ? 'Pre-built UK Compliant Template' : 'AI Generated'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>{Math.round(template.content.length / 1000)}k chars</span>
                    <span>{new Date(template.created_at).toLocaleDateString('en-GB')}</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={(e) => { e.stopPropagation(); handleViewTemplate(template); }}
                    >
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={(e) => { e.stopPropagation(); handleDownload(template); }}
                    >
                      <Download className="w-3 h-3 mr-1" /> Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Generate Policy Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Generate Policy Template
            </DialogTitle>
            <DialogDescription>
              Select a policy type to generate a UK Healthcare compliant template for ICRCL
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Policy Type *</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger data-testid="policy-type-select">
                  <SelectValue placeholder="Select a policy type..." />
                </SelectTrigger>
                <SelectContent>
                  {POLICY_TYPES.map((policy) => (
                    <SelectItem key={policy.id} value={policy.id}>
                      <div className="flex items-center gap-2">
                        <policy.icon className="w-4 h-4 text-slate-500" />
                        <span>{policy.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedType && selectedType !== 'custom' && (
              <Card className="bg-slate-50">
                <CardContent className="py-3">
                  <p className="text-sm text-slate-600">
                    {POLICY_TYPES.find(p => p.id === selectedType)?.description}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    <strong>Compliance:</strong> {POLICY_TYPES.find(p => p.id === selectedType)?.compliance}
                  </p>
                </CardContent>
              </Card>
            )}

            {selectedType === 'custom' && (
              <div className="space-y-2">
                <Label>Custom Policy Requirements *</Label>
                <Textarea
                  placeholder="Describe the policy you need. For example: 'Create a Social Media Policy for staff that covers professional boundaries, use of social media during work hours, and guidelines for online interaction with families...'"
                  value={customRequirements}
                  onChange={(e) => setCustomRequirements(e.target.value)}
                  rows={5}
                  data-testid="custom-requirements-input"
                />
                <p className="text-xs text-slate-500">
                  Be specific about what the policy should cover. The AI will ensure UK healthcare compliance.
                </p>
              </div>
            )}

            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="py-3">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Generated policies will include ICRCL company name and comply with UK healthcare regulations (CQC, Ofsted, Home Office, NHS standards). Review and customise before implementation.
                </p>
              </CardContent>
            </Card>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {generating && generationProgress && (
              <div className="flex-1 text-left">
                <p className="text-sm text-amber-700 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  {generationProgress}
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setGenerateDialogOpen(false)} disabled={generating}>
                Cancel
              </Button>
              <Button 
                onClick={handleGeneratePolicy} 
                disabled={generating || !selectedType}
                className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8f]"
                data-testid="confirm-generate-btn"
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Policy
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Policy Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1e3a5f]" />
              {selectedTemplate?.template_type?.charAt(0).toUpperCase() + selectedTemplate?.template_type?.slice(1).replace(/_/g, ' ')} Policy
            </DialogTitle>
            <div className="flex gap-2 mt-2">
              <Badge variant={selectedTemplate?.status === 'approved' ? 'default' : 'secondary'}>
                {selectedTemplate?.status || 'draft'}
              </Badge>
              <Badge variant="outline">
                {selectedTemplate?.source === 'prebuilt' ? 'Pre-built Template' : 'AI Generated'}
              </Badge>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto bg-white border rounded-lg p-6 my-4">
            <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">
              {selectedTemplate?.content}
            </pre>
          </div>

          <DialogFooter className="flex-shrink-0 gap-2">
            <Button variant="outline" onClick={() => handleCopyToClipboard(selectedTemplate?.content)}>
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            <Button variant="outline" onClick={() => handlePrint(selectedTemplate)}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button onClick={() => handleDownload(selectedTemplate)}>
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
